type WorkspaceFileValidationKey =
  | 'advisory:workspace.live.fileUnsupported'
  | 'advisory:workspace.live.fileTooLarge'
  | 'advisory:workspace.live.fileInvalid'
  | 'advisory:workspace.live.fileValidationFailed';

export class WorkspaceUploadError extends Error {
  constructor(public readonly i18nKey: WorkspaceFileValidationKey) {
    super(i18nKey);
    this.name = 'WorkspaceUploadError';
  }
}

/** Check the gateway's current file policy before creating a durable source reservation. */
export async function validateWorkspaceFile(file: File, token: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/documents/advisory/upload-validation`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          sizeBytes: file.size,
          mimeType: file.type || 'application/octet-stream',
        }),
      }
    );
  } catch {
    throw new WorkspaceUploadError('advisory:workspace.live.fileValidationFailed');
  }
  let data: { status?: unknown; detail?: { code?: unknown } } | null = null;
  try {
    data = await response.json();
  } catch {
    // A gateway/proxy error body is not trusted or shown to the user.
  }
  if (response.ok && data?.status === 'accepted') return;
  if (response.status === 413)
    throw new WorkspaceUploadError('advisory:workspace.live.fileTooLarge');
  if (response.status === 422)
    throw new WorkspaceUploadError('advisory:workspace.live.fileInvalid');
  if (response.status === 400) {
    if (data?.detail?.code === 'fileUnsupported')
      throw new WorkspaceUploadError('advisory:workspace.live.fileUnsupported');
    if (data?.detail?.code === 'fileInvalid')
      throw new WorkspaceUploadError('advisory:workspace.live.fileInvalid');
  }
  throw new WorkspaceUploadError('advisory:workspace.live.fileValidationFailed');
}

/** The gateway binds this upload to the server-owned source reservation before accepting bytes. */
export async function uploadWorkspaceFile(
  projectId: string,
  sourceId: string,
  generation: number,
  requestId: string,
  file: File,
  token: string
) {
  const form = new FormData();
  form.append('file', file);
  form.append('generation', String(generation));
  form.append('requestId', requestId);
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/documents/advisory/${encodeURIComponent(
      projectId
    )}/sources/${encodeURIComponent(sourceId)}/upload`,
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
  );
  if (!response.ok) throw new Error('uploadFailed');
  const data: unknown = await response.json();
  if (!data || typeof data !== 'object') throw new Error('uploadUncertain');
  const result = data as Record<string, unknown>;
  if (
    result.sourceId !== sourceId ||
    result.generation !== generation ||
    result.status !== 'uploaded' ||
    typeof result.documentId !== 'string'
  )
    throw new Error('uploadUncertain');
  return result;
}
